import { Link } from 'react-router-dom';

/**
 * Rodapé institucional.
 *
 * F3.2 / item 5 do checklist de bloqueio de lançamento: enquanto o site apenas
 * apresentava o laboratório, bastava o texto de marca. A partir do momento em
 * que ele **vende**, o rodapé passa a ser exigência — razão social, CNPJ,
 * endereço, responsável técnico com registro, licença sanitária e os
 * documentos legais precisam estar acessíveis de qualquer página.
 *
 * Os valores marcados como pendentes abaixo são os únicos itens que este
 * componente não pode inventar: têm de vir da proprietária. Estão visíveis de
 * propósito, para que a ausência não passe despercebida até o dia do
 * lançamento.
 */

/** Dados cadastrais. PENDENTE — confirmar com a proprietária antes do go-live. */
const CADASTRO = {
  razaoSocial: 'Laboratório Santa Izabel Medicina Laboratorial',
  cnpj: null as string | null,
  endereco: 'Rua Doutor Oswaldo Cruz, 312 — Centro, Três Rios/RJ',
  responsavelTecnico: null as { nome: string; conselho: string; registro: string } | null,
  licencaSanitaria: null as string | null,
};

const SOCIAIS = [
  {
    nome: 'Facebook',
    url: 'https://www.facebook.com/laboratoriosantaizabel',
    path: 'M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z',
  },
  {
    nome: 'Instagram',
    url: 'https://www.instagram.com/labsantaizabel/',
    path: 'M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z',
  },
];

function IconeSocial({ nome, url, path }: (typeof SOCIAIS)[number]) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`${nome} do Laboratório Santa Izabel`}
      className="w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center group transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-santa-gold focus-visible:ring-offset-2 focus-visible:ring-offset-santa-dark"
    >
      <svg
        className="w-5 h-5 text-slate-300 group-hover:text-white transition-colors"
        fill="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path fillRule="evenodd" d={path} clipRule="evenodd" />
      </svg>
    </a>
  );
}

export default function Footer() {
  const ano = new Date().getFullYear();

  return (
    <footer className="bg-santa-dark text-slate-300 py-16 border-t border-slate-800">
      <div className="container mx-auto px-4 md:px-8 text-center md:text-left">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          <div>
            <div className="flex justify-center md:justify-start mb-6">
              <div className="bg-white rounded-xl p-3 inline-block">
                <img
                  src="/logo.png"
                  alt="Santa Izabel Medicina Laboratorial"
                  className="h-28 w-auto"
                  width={224}
                  height={112}
                />
              </div>
            </div>
            <p className="text-sm text-slate-400 mb-6">
              Mais de três décadas como referência de qualidade em medicina laboratorial. O primeiro
              laboratório a ser aprovado pelo sistema nacional de acreditação em Três Rios.
            </p>

            <div className="flex items-center justify-center md:justify-start gap-4">
              {SOCIAIS.map((s) => (
                <IconeSocial key={s.nome} {...s} />
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-white font-medium mb-4">Acesso Rápido</h4>
            <ul className="space-y-3 text-sm">
              <li>
                <Link to="/sobre" className="hover:text-white transition-colors">
                  Sobre Nós
                </Link>
              </li>
              <li>
                <Link to="/exames" className="hover:text-white transition-colors">
                  Nossos Exames
                </Link>
              </li>
              <li>
                <Link to="/unidades" className="hover:text-white transition-colors">
                  Unidades
                </Link>
              </li>
              <li>
                <Link to="/viver-mais" className="hover:text-amber-400 transition-colors">
                  Cartão Viver Mais
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-white font-medium mb-4">Horários</h4>
            <ul className="space-y-3 text-sm text-slate-400">
              <li>
                Segunda a Sexta:
                <br />
                <span className="text-white">6:30 às 17:00h</span>
              </li>
              <li>
                Sábado:
                <br />
                <span className="text-white">7:00 às 11:00h</span>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-white font-medium mb-4">Atendimento</h4>
            <ul className="space-y-3 text-sm">
              <li>
                <Link to="/contato" className="hover:text-white transition-colors">
                  Fale Conosco
                </Link>
              </li>
              <li>
                <a
                  href="https://santaizabel.shiftcloud.com.br/shift/lis/santaizabel/elis/s01.iu.web.Login.cls"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-white transition-colors"
                >
                  Resultados Online
                </a>
              </li>
              <li>
                <a href="tel:+552422521692" className="hover:text-white transition-colors">
                  (24) 2252-1692
                </a>
              </li>
            </ul>

            <h4 className="text-white font-medium mb-4 mt-8">Documentos</h4>
            <ul className="space-y-3 text-sm">
              <li>
                <Link to="/termos" className="hover:text-white transition-colors">
                  Termos de Uso e Venda
                </Link>
              </li>
              <li>
                <Link to="/privacidade" className="hover:text-white transition-colors">
                  Política de Privacidade
                </Link>
              </li>
              <li>
                <Link to="/cancelamento" className="hover:text-white transition-colors">
                  Cancelamento e Reembolso
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/*
          Identificação legal do estabelecimento. Obrigatória a partir do momento
          em que o site vende, e verificada no gate G4.
        */}
        <div className="mt-16 pt-8 border-t border-slate-800 text-xs text-slate-500 leading-relaxed">
          <p className="mb-1">
            <span className="text-slate-400">{CADASTRO.razaoSocial}</span>
            {CADASTRO.cnpj ? (
              <> — CNPJ {CADASTRO.cnpj}</>
            ) : (
              <span className="text-amber-500/70"> — CNPJ pendente de cadastro</span>
            )}
          </p>
          <p className="mb-1">{CADASTRO.endereco}</p>
          <p className="mb-1">
            {CADASTRO.responsavelTecnico ? (
              <>
                Responsável Técnico: {CADASTRO.responsavelTecnico.nome} —{' '}
                {CADASTRO.responsavelTecnico.conselho} {CADASTRO.responsavelTecnico.registro}
              </>
            ) : (
              <span className="text-amber-500/70">Responsável Técnico pendente de cadastro</span>
            )}
            {CADASTRO.licencaSanitaria ? (
              <> · Licença Sanitária {CADASTRO.licencaSanitaria}</>
            ) : (
              <span className="text-amber-500/70"> · Licença Sanitária pendente de cadastro</span>
            )}
          </p>
        </div>

        <div className="mt-6 pt-6 border-t border-slate-800 text-sm text-slate-500 flex flex-col md:flex-row items-center justify-between gap-2">
          <p>© {ano} Santa Izabel Medicina Laboratorial. Todos os direitos reservados.</p>
          <p className="text-xs text-slate-600">
            Os resultados de exames são disponibilizados exclusivamente no portal do laboratório.
          </p>
        </div>
      </div>
    </footer>
  );
}
